import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Flex, Text, Tooltip, useDisclosure } from "@chakra-ui/react";
import { FocusLibraryDrawer } from "@/components/FocusLibraryDrawer";
import { EditingFocus } from "@/components/FocusLibraryDrawer/FocusPage";
import { FocusDTO } from "@/js/api/focus";
import {
	getSignaturesForFocusResourcePicker,
	transformFocusDtoToEditingFocus,
} from "@/components/FocusLibraryDrawer/FocusResourcePicker/utils";
import useStore from "@/stores/index";
import { colours } from "@/utils/themeUtils";
import { useFocusStore } from "@/stores/v2/focusStore";
import { useFeatureFlagsQuery } from "@/query/featureFlagsQuery";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";
import { FeatureFlag } from "@/api/classroomFeatureFlags";
import { ChakraFAIcon } from "@/components/ui/Icon";

interface Props {
	focus: EditingFocus;
	isUpdatingFocus: boolean;
	classIsActive: boolean;
	endFocus: (focusId: string) => void;
	bubbleUpdatedFocus: (focus: EditingFocus) => void;
}

export function AppliedFocus(props: Props): JSX.Element {
	const focusLibraryDrawer = useDisclosure();

	const focusStore = useFocusStore();
	const userSessionStore = useUserSessionStore();
	const featureFlagsQuery = useFeatureFlagsQuery(userSessionStore.active_device as string);

	const [appliedFocus, setAppliedFocus] = useState<EditingFocus>(props.focus);

	// Stops the drawer being re-rendered when appliedFocus is updated
	// unless the actual value changes. This prevents the drawer from being
	// re-rendered every time the class screenshots are updated.
	const memoisedAppliedFocus: EditingFocus = useMemo(
		() => ({
			...appliedFocus,
			active: true,
		}),
		[appliedFocus]
	);

	function handleClickEditFocus() {
		focusStore.setOpenedDirectlyToEdit(true);
		focusLibraryDrawer.onOpen();
	}

	function handleClickEndFocus() {
		props.endFocus(appliedFocus.id);
	}

	useEffect(() => {
		setAppliedFocus(props.focus);
	}, [props.focus]);

	const store = useStore();
	const allSignatureResourceOptions = getSignaturesForFocusResourcePicker(store.signatures);

	return (
		<>
			<Flex background={props.classIsActive ? colours.blue[600] : colours.navy[800]} mr={10} rounded={"md"} color={"white"} px={2} py={0.5}>
				<Flex alignItems={"center"} ml={3} mr={5} gap={3}>
					<ChakraFAIcon icon={"fa-arrows-to-eye"} variant="solid" />
					<Text fontWeight={"medium"} data-testid="applied-focus-label">
						In Focus{appliedFocus.name && `: ${appliedFocus.name}`}
					</Text>
					{featureFlagsQuery.data?.includes(FeatureFlag.FOCUS_LOCK_MODE) && appliedFocus.tabsLocked && (
						<Flex
							background={"#0075DB"}
							px={2}
							py={0.5}
							rounded={"md"}
							alignItems={"center"}
							gap={1}
							fontSize={"xs"}
							fontWeight={"medium"}
							ml={1}
						>
							<ChakraFAIcon icon={"fa-lock"} variant="solid" />
							<Text>LOCKED</Text>
						</Flex>
					)}
				</Flex>
				<Box borderLeft={"1px solid rgba(255, 255, 255, 0.5)"}></Box>
				<Box>
					<Tooltip label="Click to edit focus resources">
						<Button
							variant={"unstyled"}
							cursor={"pointer"}
							background={"inherit"}
							color={"inherit"}
							onClick={handleClickEditFocus}
							size={"sm"}
							mx={2}
							aria-label="Click to edit focus resources"
							data-testid="edit-applied-focus-button"
						>
							<ChakraFAIcon icon={"fa-pencil"} fontSize={"sm"} variant="solid" />
						</Button>
					</Tooltip>
				</Box>
				<Box borderLeft={"1px solid rgba(255, 255, 255, 0.5)"}></Box>
				<Box>
					<Tooltip label="Click to end focus">
						<Button
							variant={"unstyled"}
							cursor={"pointer"}
							background={"inherit"}
							color={"inherit"}
							onClick={handleClickEndFocus}
							size={"sm"}
							ml={2}
							isDisabled={props.isUpdatingFocus}
							_disabled={{ cursor: "not-allowed", background: "inherit", color: "inherit" }}
							aria-label="Click to end focus"
							data-testid="end-applied-focus-button"
						>
							<ChakraFAIcon icon={"fa-times"} />
						</Button>
					</Tooltip>
				</Box>
			</Flex>
			<FocusLibraryDrawer
				isOpen={focusLibraryDrawer.isOpen}
				closeDrawer={focusLibraryDrawer.onClose}
				focus={memoisedAppliedFocus}
				bubbleAppliedOrUpdatedFocus={(appliedFocus: FocusDTO) => {
					const updatedFocus = transformFocusDtoToEditingFocus(appliedFocus, allSignatureResourceOptions);
					setAppliedFocus(updatedFocus);
					props.bubbleUpdatedFocus(updatedFocus);
				}}
			/>
		</>
	);
}
